import { useState, useEffect } from 'react';
import api from '../../utils/api';
import toast from 'react-hot-toast';

const EMPTY = { relationship: '', name: '', sex: '', birth_date: '', id_no: '', living_status: 'living', photo_path: null, prove_image_path: null, signature_path: null };

function FamilyModal({ initial, onClose, onSave }) {
  const [form, setForm] = useState(initial || EMPTY);
  const [photoFile, setPhotoFile] = useState(null);
  const [proveFile, setProveFile] = useState(null);
  const [signatureFile, setSignatureFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(initial?.photo_path ? `https://ems.ourschool.lk/uploads/${initial.photo_path}` : null);
  const [provePreview, setProvePreview] = useState(initial?.prove_image_path ? `https://ems.ourschool.lk/uploads/${initial.prove_image_path}` : null);
  const [signaturePreview, setSignaturePreview] = useState(initial?.signature_path ? `https://ems.ourschool.lk/uploads/${initial.signature_path}` : null);
  const [saving, setSaving] = useState(false);
  const set = k => e => setForm(p => ({ ...p, [k]: e.target.value }));

  useEffect(() => {
    setForm(initial || EMPTY);
    setPhotoFile(null);
    setProveFile(null);
    setSignatureFile(null);
    setPhotoPreview(initial?.photo_path ? `https://ems.ourschool.lk/uploads/${initial.photo_path}` : null);
    setProvePreview(initial?.prove_image_path ? `https://ems.ourschool.lk/uploads/${initial.prove_image_path}` : null);
    setSignaturePreview(initial?.signature_path ? `https://ems.ourschool.lk/uploads/${initial.signature_path}` : null);
  }, [initial]);

  const submit = async () => {
    if (!form.relationship || !form.name.trim()) {
      toast.error('Relationship and name are required');
      return;
    }
    setSaving(true);
    try {
      await onSave(form, photoFile, proveFile, signatureFile);
      onClose();
    } finally {
      setSaving(false);
    }
  };

  const handlePhotoSelect = e => {
    const file = e.target.files[0];
    if (!file) return;
    setPhotoFile(file);
    setPhotoPreview(URL.createObjectURL(file));
  };

  const handleProveSelect = e => {
    const file = e.target.files[0];
    if (!file) return;
    setProveFile(file);
    setProvePreview(URL.createObjectURL(file));
  };

  const handleSignatureSelect = e => {
    const file = e.target.files[0];
    if (!file) return;
    setSignatureFile(file);
    setSignaturePreview(URL.createObjectURL(file));
  };

  return (
    <div className="modal-backdrop">
      <div className="modal" style={{ maxWidth: 560 }}>
        <div className="modal-header">
          <h3>{initial?.id ? 'Edit Family Member' : 'Add Family Member'}</h3>
          <button className="btn btn-ghost btn-sm" onClick={onClose}>✕</button>
        </div>
        <div className="modal-body">
          <div className="form-group">
            <label className="form-label">Relationship *</label>
            <select className="form-select" value={form.relationship} onChange={set('relationship')}>
              <option value="">— Select —</option>
              <option value="wife">Wife</option>
              <option value="husband">Husband</option>
              <option value="father">Father</option>
              <option value="mother">Mother</option>
              <option value="brother">Brother</option>
              <option value="sister">Sister</option>
              <option value="son">Son</option>
              <option value="daughter">Daughter</option>
            </select>
          </div>
          <div className="form-group">
            <label className="form-label">Full Name *</label>
            <input className="form-input" value={form.name} onChange={set('name')} placeholder="Full name" />
          </div>
          <div className="form-group">
            <label className="form-label">ID Number</label>
            <input className="form-input" value={form.id_no} onChange={set('id_no')} placeholder="ID number" />
          </div>
          <div className="grid-2">
            <div className="form-group">
              <label className="form-label">Sex</label>
              <select className="form-select" value={form.sex} onChange={set('sex')}>
                <option value="">— Select —</option>
                <option value="male">Male</option>
                <option value="female">Female</option>
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Birth Date</label>
              <input className="form-input" type="date" value={form.birth_date ? form.birth_date.split('T')[0] : ''} onChange={set('birth_date')} />
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">Living Status</label>
            <select className="form-select" value={form.living_status} onChange={set('living_status')}>
              <option value="living">Living</option>
              <option value="deceased">Deceased</option>
            </select>
          </div>
          <div className="form-group">
            <label className="form-label">Photo</label>
            <input className="form-input" type="file" accept="image/*" onChange={handlePhotoSelect} />
            {photoPreview && (
              <div style={{ marginTop: 12 }}>
                <img src={photoPreview} alt="Family member" style={{ width: 120, borderRadius: 8, objectFit: 'cover' }} />
              </div>
            )}
          </div>
          <div className="form-group">
            <label className="form-label">Prove Image</label>
            <input className="form-input" type="file" accept="image/*" onChange={handleProveSelect} />
            {provePreview && (
              <div style={{ marginTop: 12 }}>
                <img src={provePreview} alt="Proof" style={{ width: 120, borderRadius: 8, objectFit: 'cover' }} />
              </div>
            )}
          </div>
          <div className="form-group">
            <label className="form-label">Signature Image</label>
            <input className="form-input" type="file" accept="image/*" onChange={handleSignatureSelect} />
            {signaturePreview && (
              <div style={{ marginTop: 12 }}>
                <img src={signaturePreview} alt="Signature" style={{ width: 120, borderRadius: 8, objectFit: 'cover' }} />
              </div>
            )}
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={submit} disabled={saving}>
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function Family({ member }) {
  const [rows, setRows]     = useState([]);
  const [loading, setLoad]  = useState(false);
  const [modal, setModal]   = useState(null);
  const [deleting, setDel]  = useState(null);

  useEffect(() => {
    if (!member?.id) return;
    setLoad(true);
    api.get(`/family/${member.id}`)
      .then(r => setRows(r.data))
      .catch(() => toast.error('Failed to load family'))
      .finally(() => setLoad(false));
  }, [member?.id]);

  const uploadFamilyPhoto = async (id, photoFile) => {
    const fd = new FormData();
    fd.append('photo', photoFile);
    const { data } = await api.post(`/family/${id}/upload`, fd);
    return data;
  };

  const uploadFamilyProveImage = async (id, proveFile) => {
    const fd = new FormData();
    fd.append('prove_image', proveFile);
    const { data } = await api.post(`/family/${id}/upload`, fd);
    return data;
  };

  const uploadFamilySignature = async (id, signatureFile) => {
    const fd = new FormData();
    fd.append('signature', signatureFile);
    const { data } = await api.post(`/family/${id}/upload`, fd);
    return data;
  };

  const handleSave = async (form, photoFile, proveFile, signatureFile) => {
    if (modal.row?.id) {
      const { data } = await api.put(`/family/${modal.row.id}`, form);
      let saved = data;
      if (photoFile) saved = await uploadFamilyPhoto(data.id, photoFile);
      if (proveFile) saved = await uploadFamilyProveImage(saved.id, proveFile);
      if (signatureFile) saved = await uploadFamilySignature(saved.id, signatureFile);
      setRows(p => p.map(r => r.id === saved.id ? saved : r));
      toast.success('Updated');
    } else {
      const { data } = await api.post('/family', { member_id: member.id, ...form });
      let saved = data;
      if (photoFile) saved = await uploadFamilyPhoto(data.id, photoFile);
      if (proveFile) saved = await uploadFamilyProveImage(saved.id, proveFile);
      if (signatureFile) saved = await uploadFamilySignature(saved.id, signatureFile);
      setRows(p => [...p, saved]);
      toast.success('Added');
    }
  };

  const handleDelete = async id => {
    setDel(id);
    try {
      await api.delete(`/family/${id}`);
      setRows(p => p.filter(r => r.id !== id));
      toast.success('Deleted');
    } catch {
      toast.error('Delete failed');
    } finally {
      setDel(null);
    }
  };

  if (!member?.id) return (
    <div className="empty-state">
      <p>Save Personal info first to manage family members.</p>
    </div>
  );

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16 }}>
        <button className="btn btn-primary" onClick={() => setModal({ row: null })}>
          + Add Family Member
        </button>
      </div>

      {loading ? (
        <p style={{ color: 'var(--text3)', textAlign: 'center', padding: 32 }}>Loading…</p>
      ) : rows.length === 0 ? (
        <div className="empty-state"><p>No family members added yet.</p></div>
      ) : (
        <div className="card" style={{ overflow: 'hidden' }}>
          <table>
            <thead>
              <tr>
                <th>Photo</th>
                <th>Relationship</th>
                <th>Name</th>
                <th>ID No</th>
                <th>Sex</th>
                <th>Birth Date</th>
                <th>Living Status</th>
                <th>Images</th>
                <th style={{ width: 110 }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(row => (
                <tr key={row.id}>
                  <td style={{ padding: 8 }}>
                    {row.photo_path ? (
                      <img
                        src={`https://ems.ourschool.lk/uploads/${row.photo_path}`}
                        alt={row.name}
                        style={{ width: 40, height: 40, borderRadius: '50%', objectFit: 'cover' }}
                      />
                    ) : (
                      <div style={{ width: 40, height: 40, borderRadius: '50%', background: 'var(--surface2)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text3)', fontSize: 12 }}>
                        —
                      </div>
                    )}
                  </td>
                  <td><span className="badge badge-blue" style={{ textTransform: 'capitalize' }}>{row.relationship}</span></td>
                  <td>{row.name}</td>
                  <td>{row.id_no || '—'}</td>
                  <td style={{ textTransform: 'capitalize' }}>{row.sex || '—'}</td>
                  <td>{row.birth_date ? row.birth_date.split('T')[0] : '—'}</td>
                  <td><span className={`badge badge-${row.living_status === 'living' ? 'green' : 'red'}`}>{row.living_status}</span></td>
                  <td>
                    <div style={{ display: 'flex', gap: 4 }}>
                      {row.prove_image_path && <a href={`https://ems.ourschool.lk/uploads/${row.prove_image_path}`} target="_blank" rel="noreferrer" className="badge badge-blue" style={{ fontSize: 11 }}>Prove</a>}
                      {row.signature_path && <a href={`https://ems.ourschool.lk/uploads/${row.signature_path}`} target="_blank" rel="noreferrer" className="badge badge-purple" style={{ fontSize: 11 }}>Sig</a>}
                    </div>
                  </td>
                  <td>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <button className="btn btn-secondary btn-sm" onClick={() => setModal({ row })}>Edit</button>
                      <button className="btn btn-danger btn-sm" disabled={deleting === row.id}
                        onClick={() => window.confirm('Delete this family member?') && handleDelete(row.id)}>
                        {deleting === row.id ? '…' : 'Del'}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {modal !== null && (
        <FamilyModal
          initial={modal.row}
          onClose={() => setModal(null)}
          onSave={handleSave}
        />
      )}
    </div>
  );
}
